import React, {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
} from "@vis.gl/react-google-maps";

import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  MIN_ZOOM,
  MAX_ZOOM,
  latitudeDeltaToZoom,
  getMapColors,
  SATELLITE_DIM_OPACITY,
  SATELLITE_DIM_TILE_URL,
  type MapTileTheme,
} from "./map-styles";
import { useMapTileTheme } from "./use-map-tile-theme";
import {
  Box,
  colorVar,
  Icon,
  MAP_MARKER_EXIT_MS,
  MAP_PIN_WIDTH,
  MapClusterGlyph,
  MapInfoboxCaret,
  MapInfoboxTransition,
  MapPinGlyph,
  useIsTouchDevice,
  type IconName,
} from "@salestrak/ui";
import { UserLocationMarker } from "./user-location-marker";
import "./map-markers.css";
import { useMarkerPresence } from "./use-marker-presence";
import { MapInfoboxContent, INFOBOX_WIDTH } from "./map-infobox-content";
import { MAP_INFOBOX_ID } from "./use-click-away-pass-through";
import {
  centeredOverlayLeft,
  getClusterAtts,
  interpolateCameraCenter,
  isCoordinateInInsetBounds,
} from "./map-utils";
import type {
  MapPin,
  MapClusterBubble,
  Region,
  MapViewRef,
  MapSurfaceProps,
} from "./map-types";
import { getEnv } from "#app/lib/env";
import { omitUndefined, type Omittable } from "@salestrak/ui";

export type { MapPin, MapClusterBubble, Region, MapViewRef } from "./map-types";

const API_KEY = getEnv("EXPO_PUBLIC_GOOGLE_API_KEY");
const MAP_ID = "90f87356969d889c";

// How long a removed marker stays mounted to play its fade/shrink-out before
// being dropped. Matches the map-marker particle's exit duration.
const MARKER_EXIT_MS = MAP_MARKER_EXIT_MS;
const CAMERA_FOCUS_DURATION_MS = 500;
const SELECTION_REVEAL_INSET = 0.18;
// Where a selection flight PARKS the pin: this fraction of the map's height
// below center (~72% down). The infobox opens upward from the pin, so the
// space above it — not the pin's centering — is what the flight must buy.
const SELECTION_PARK_FRACTION = 0.22;

/** Under this many degrees (~1 cm) a flight's landing IS the current center. */
const CAMERA_SETTLED_DEG = 1e-7;

const getPinKey = (pin: MapPin) => pin.id;
const getClusterKey = (cluster: MapClusterBubble) => cluster.id;

// Registering a (no-op) click listener makes the marker clickable, which is
// what tells vis.gl to enable pointer-events on the infobox content.
// Module-scope so the listener isn't re-registered every render.
const consumeMarkerClick = () => {};

function SelectionEntranceGate({
  map,
  pin,
  children,
}: {
  map: google.maps.Map | null;
  pin: MapPin;
  children: React.ReactNode;
}): React.ReactElement | null {
  const [ready, setReady] = useState(false);
  const { latitude, longitude } = pin;

  useEffect(() => {
    if (!map) return;

    let listener: google.maps.MapsEventListener | null = null;
    const revealWhenVisible = () => {
      const bounds = map.getBounds();
      if (!bounds) return;
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();
      if (
        !isCoordinateInInsetBounds(
          { latitude, longitude },
          {
            north: northEast.lat(),
            south: southWest.lat(),
            east: northEast.lng(),
            west: southWest.lng(),
          },
          SELECTION_REVEAL_INSET,
        )
      ) {
        return;
      }
      setReady(true);
      listener?.remove();
      listener = null;
    };

    listener = map.addListener("bounds_changed", revealWhenVisible);
    revealWhenVisible();
    return () => listener?.remove();
  }, [latitude, longitude, map]);

  return ready ? <>{children}</> : null;
}

// Stop the infobox's presses from reaching the map. Google Maps synthesizes
// its map `click` from the down/up events that bubble to its container, so
// stopping those here means presses inside the infobox can never be taken
// for map clicks (which create a pin). DOM `click` itself must keep
// bubbling — React's delegated handlers for the card's buttons sit above
// the map container. Listeners die with the node; no cleanup needed.
//
// The click that does bubble reaches the marker hosting the infobox, and a
// clickable AdvancedMarker FOCUSES ITSELF after every click inside it
// (Google's keyboard grammar for markers). Inside the infobox that click was
// a press on a control, and the steal lands after the press has done its
// work — a non-modal popover closes on focus-out, an editor blurs. This
// marker exists only to turn pointer-events on (its click listener is a
// no-op), so it gives up focusing itself. Resolved on the first click, not at
// mount: vis.gl attaches the content to the marker after this ref runs.
const CONTAINED_EVENTS = [
  "pointerdown", "pointerup", "mousedown", "mouseup",
  "touchstart", "touchend", "dblclick",
] as const;
const containMapEvents = (node: HTMLElement | null) => {
  if (!node) return;
  for (const type of CONTAINED_EVENTS) {
    node.addEventListener(type, (e: Event) => e.stopPropagation());
  }
  node.addEventListener("click", () => {
    const marker = node.closest("gmp-advanced-marker");
    if (marker instanceof HTMLElement) marker.focus = () => {};
  });
};

// The overlay glyphs on a pin's disc are painted with the map palette's
// `pinOverlayText` rather than a theme token: they sit on the fixed
// `pinOverlay` scrim over an identity color, which wears white ink in both
// modes. Icon paints `currentColor`, so the value rides in as `color`.
const overlayIconStyle = (color: string) => ({ color });

function NewLocationPinMarker({
  latitude,
  longitude,
  outlineColor,
  pinOverlay,
  pinOverlayText,
  children,
}: {
  latitude: number;
  longitude: number;
  outlineColor: string;
  pinOverlay: string;
  pinOverlayText: string;
  children?: React.ReactNode;
}) {
  return (
    <AdvancedMarker
      position={{ lat: latitude, lng: longitude }}
      zIndex={999}
      onClick={consumeMarkerClick}
    >
      <MapPinGlyph
        color="var(--primary)"
        outlineColor={outlineColor}
        overlayColor={pinOverlay}
      >
        <Icon name="plus" size="lg" style={overlayIconStyle(pinOverlayText)} />
      </MapPinGlyph>
      {children}
    </AdvancedMarker>
  );
}

// ─── Marker components ───────────────────────────────────────────────────────
//
// Both markers are memoized and hand vis.gl STABLE props. `AdvancedMarker`
// binds `position` with an effect keyed on the object — a literal rebuilt on
// every render writes `marker.position` on every render — and re-registers
// its click listener whenever `onClick` is a new function. Selecting one pin
// re-renders the map screen, and with a few hundred markers on the map that
// was a few hundred Google writes per pass; memoized, a re-render that changes
// nothing about a marker costs it nothing. The rows themselves keep identity
// across viewport answers (`useMapClusters`), so a repeated pin is the same
// object here too.

const SELECTED_PIN_Z_INDEX = 998;

const PinMarker = memo(function PinMarker({
  pin,
  outlineColor,
  pinOverlay,
  pinOverlayText,
  onClick,
  onHover,
  exiting,
  selected = false,
}: Omittable<{
  pin: MapPin;
  outlineColor: string;
  pinOverlay: string;
  pinOverlayText: string;
  onClick?: (pin: MapPin) => void;
  /** Pointer at rest on the glyph (vis.gl's `onMouseEnter`). */
  onHover?: (pin: MapPin) => void;
  exiting?: boolean;
  selected?: boolean;
}>) {
  const showIcon = pin.count === 1 && !!pin.icon;
  const displayText = pin.count > 9 ? "9+" : String(pin.count);
  const countFontSize = pin.count > 9 ? 11 : 14;
  const position = useMemo(
    () => ({ lat: pin.latitude, lng: pin.longitude }),
    [pin.latitude, pin.longitude],
  );
  const handleClick = useCallback(() => onClick?.(pin), [onClick, pin]);
  const handleHover = useCallback(() => onHover?.(pin), [onHover, pin]);

  return (
    // `omitUndefined` because a non-clickable marker must DELETE `onClick` —
    // `AdvancedMarker`'s props predate `exactOptionalPropertyTypes`.
    <AdvancedMarker
      {...omitUndefined({
        position,
        onClick: exiting || !onClick ? undefined : handleClick,
        onMouseEnter: exiting || !onHover ? undefined : handleHover,
        // The lifted pin draws over every other marker (unset z-indexes stack
        // by screen position, so a small value can still lose), but under the
        // draft pin (999) and the infobox (1000).
        zIndex: selected ? SELECTED_PIN_Z_INDEX : undefined,
      })}
    >
      <MapPinGlyph
        accessibleLabel={pin.wholeAddress ?? pin.addressLine1}
        color={colorVar(pin.color)}
        exiting={exiting}
        label={showIcon ? undefined : displayText}
        labelColor={pinOverlayText}
        labelFontSize={countFontSize}
        outlineColor={outlineColor}
        overlayColor={pinOverlay}
        selected={selected}
      >
        {/* Workflow icon overlay — the particle centers it on the disc. */}
        {showIcon ? (
          <Icon
            name={pin.icon as IconName}
            size="lg"
            style={overlayIconStyle(pinOverlayText)}
          />
        ) : null}
      </MapPinGlyph>
    </AdvancedMarker>
  );
});

const ClusterMarker = memo(function ClusterMarker({
  cluster,
  outlineColor,
  clusterFill,
  clusterText,
  onClick,
  exiting,
}: Omittable<{
  cluster: MapClusterBubble;
  outlineColor: string;
  clusterFill: string;
  clusterText: string;
  onClick: (bounds: MapClusterBubble["bounds"]) => void;
  exiting?: boolean;
}>) {
  const { dimension, fontSize, strokeWidth } = getClusterAtts(cluster.count);
  const position = useMemo(
    () => ({ lat: cluster.latitude, lng: cluster.longitude }),
    [cluster.latitude, cluster.longitude],
  );
  const handleClick = useCallback(
    () => onClick(cluster.bounds),
    [onClick, cluster.bounds],
  );

  return (
    // `omitUndefined` because a non-clickable marker must DELETE `onClick` —
    // `AdvancedMarker`'s props predate `exactOptionalPropertyTypes`.
    <AdvancedMarker
      {...omitUndefined({
        position,
        onClick: exiting ? undefined : handleClick,
        zIndex: 1,
      })}
    >
      <MapClusterGlyph
        color={clusterFill}
        count={cluster.count}
        exiting={exiting}
        fontSize={fontSize}
        outlineColor={outlineColor}
        size={dimension}
        strokeWidth={strokeWidth}
        textColor={clusterText}
      />
    </AdvancedMarker>
  );
});

// React 19 ref-as-prop — the app does not use forwardRef.
function MapInternals({
  isDark,
  mapSettings,
  pins,
  clusters,
  onRegionChangeComplete,
  onInteractionStart,
  onClusterPress,
  onPinPress,
  onPinHover,
  userLocation,
  selectedPin,
  showInfobox = true,
  onInfoboxClose,
  onAddProject,
  onMapClick,
  tileTheme,
  ref,
}: MapSurfaceProps &
  Omittable<{ tileTheme: MapTileTheme; ref?: React.Ref<MapViewRef> }>) {
  const map = useMap();
  // Queue imperative calls that fire before `map` is ready (e.g. when the
  // map screen mounts via cross-route navigation and the consumer invokes
  // focusWithOffset on the very first render). Drained when map becomes
  // non-null.
  const pendingCallsRef = useRef<Array<(m: google.maps.Map) => void>>([]);
  const onInteractionStartRef = useRef(onInteractionStart);
  onInteractionStartRef.current = onInteractionStart;
  const cameraAnimationFrameRef = useRef<number | null>(null);
  const cancelCameraAnimation = useCallback(() => {
    if (cameraAnimationFrameRef.current === null) return;
    cancelAnimationFrame(cameraAnimationFrameRef.current);
    cameraAnimationFrameRef.current = null;
  }, []);
  const animateCameraCenter = useCallback(
    (
      mapInstance: google.maps.Map,
      target: { latitude: number; longitude: number },
      horizontalOffsetPx = 0,
      verticalOffsetPx = 0,
      duration = CAMERA_FOCUS_DURATION_MS,
    ) => {
      cancelCameraAnimation();

      const center = mapInstance.getCenter();
      const zoom = mapInstance.getZoom() ?? DEFAULT_ZOOM;
      if (!center) return;

      const from = {
        latitude: center.lat(),
        longitude: center.lng(),
      };
      // A flight to where the camera already sits (the same pin selected
      // again) is skipped outright: an animation that ends with no camera
      // change draws no `idle` from Google, and the marker buffer frozen
      // below would wait for one.
      const landing = interpolateCameraCenter(
        from,
        target,
        zoom,
        horizontalOffsetPx,
        verticalOffsetPx,
        1,
      );
      if (
        Math.abs(landing.latitude - from.latitude) < CAMERA_SETTLED_DEG &&
        Math.abs(landing.longitude - from.longitude) < CAMERA_SETTLED_DEG
      ) {
        return;
      }
      const moveToProgress = (progress: number) => {
        const next = interpolateCameraCenter(
          from,
          target,
          zoom,
          horizontalOffsetPx,
          verticalOffsetPx,
          progress,
        );
        mapInstance.moveCamera({
          center: { lat: next.latitude, lng: next.longitude },
        });
      };

      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (reduceMotion || duration <= 0) {
        moveToProgress(1);
        return;
      }

      // The flight is an interaction like a drag: the marker buffer holds
      // incoming rows until the map goes idle, so a viewport answer landing
      // mid-flight cannot re-render every marker under the moving camera.
      onInteractionStartRef.current?.();
      const startedAt = performance.now();
      const step = (now: number) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        moveToProgress(progress);
        if (progress < 1) {
          cameraAnimationFrameRef.current = requestAnimationFrame(step);
        } else {
          cameraAnimationFrameRef.current = null;
        }
      };
      cameraAnimationFrameRef.current = requestAnimationFrame(step);
    },
    [cancelCameraAnimation],
  );
  useEffect(() => cancelCameraAnimation, [cancelCameraAnimation]);
  useEffect(() => {
    if (!map) return;
    const queued = pendingCallsRef.current;
    pendingCallsRef.current = [];
    queued.forEach((fn) => fn(map));
  }, [map]);
  const mapColors = getMapColors(isDark);
  // Keep removed markers mounted long enough to animate out (enter animation
  // is handled per-marker by the glyph's own transition on mount).
  const clusterPresence = useMarkerPresence(clusters, getClusterKey, MARKER_EXIT_MS);
  const pinPresence = useMarkerPresence(pins, getPinKey, MARKER_EXIT_MS);
  const onRegionChangeRef = useRef(onRegionChangeComplete);
  onRegionChangeRef.current = onRegionChangeComplete;
  const onMapClickRef = useRef(onMapClick);
  onMapClickRef.current = onMapClick;
  const selectedPinRef = useRef(selectedPin);
  selectedPinRef.current = selectedPin;

  const handleAddProject = useCallback(() => {
    if (selectedPinRef.current && onAddProject) {
      onAddProject(selectedPinRef.current);
    }
  }, [onAddProject]);

  // Apply map styles via StyledMapType on mount. The parent Map component
  // remounts (via key) whenever settings OR the tile theme change, so this
  // runs fresh each time. It cannot restyle in place: with a mapId present
  // (which AdvancedMarker requires) Google supports a client StyledMapType
  // only as the initial map type — swapping in a second one repaints the
  // geometry but permanently drops every label.
  useEffect(() => {
    if (!map) return;

    if (mapSettings.mapTypeId === "hybrid") {
      // Both are Google's own types, so the label switch flips in place.
      map.setMapTypeId(mapSettings.satelliteLabels ? "hybrid" : "satellite");
      return;
    }

    const labelStyles = [
      {
        featureType: "road",
        elementType: "labels",
        stylers: [{ visibility: mapSettings.roadNames ? "on" : "off" }],
      },
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: mapSettings.pointsOfInterest ? "on" : "off" }],
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: mapSettings.transitLines ? "on" : "off" }],
      },
    ];
    const styledMapType = new google.maps.StyledMapType([
      ...tileTheme.style,
      ...labelStyles,
    ]);
    map.mapTypes.set("customized", styledMapType);
    map.setMapTypeId("customized");
  }, [map, tileTheme, mapSettings]);

  // Traffic is an overlay, not a style: it rides either base map and toggles
  // in place without the remount the styled settings need (it is kept out of
  // the Map key for that reason).
  useEffect(() => {
    if (!map || !mapSettings.traffic) return;
    const layer = new google.maps.TrafficLayer();
    layer.setMap(map);
    return () => layer.setMap(null);
  }, [map, mapSettings.traffic]);

  // The satellite dim is an overlay map type: it sits above the imagery (and
  // Google's own hybrid labels) but below every marker and DOM island, so the
  // pins keep full contrast while the ground steps back. Same in-place
  // toggle grammar as traffic — no remount, kept out of the Map key.
  useEffect(() => {
    if (!map || mapSettings.mapTypeId !== "hybrid" || !mapSettings.dimSatellite) {
      return;
    }
    const scrim = new google.maps.ImageMapType({
      getTileUrl: () => SATELLITE_DIM_TILE_URL,
      tileSize: new google.maps.Size(256, 256),
      opacity: SATELLITE_DIM_OPACITY,
      name: "Satellite dim",
    });
    map.overlayMapTypes.push(scrim);
    return () => {
      const overlays = map.overlayMapTypes;
      for (let i = overlays.getLength() - 1; i >= 0; i--) {
        if (overlays.getAt(i) === scrim) overlays.removeAt(i);
      }
    };
  }, [map, mapSettings.mapTypeId, mapSettings.dimSatellite]);

  useImperativeHandle(
    ref,
    () => ({
      panTo(
        coords: { latitude: number; longitude: number },
        duration = CAMERA_FOCUS_DURATION_MS,
      ) {
        const run = (m: google.maps.Map) =>
          animateCameraCenter(m, coords, 0, 0, duration);
        if (!map) {
          pendingCallsRef.current.push(run);
          return;
        }
        run(map);
      },
      animateToRegion(
        region: Region,
        duration = CAMERA_FOCUS_DURATION_MS,
      ) {
        if (!map) {
          pendingCallsRef.current.push((m) => {
            m.panTo({ lat: region.latitude, lng: region.longitude });
            m.setZoom(latitudeDeltaToZoom(region.latitudeDelta));
          });
          return;
        }
        const fromCenter = map.getCenter();
        if (!fromCenter) return;

        const fromZoom = map.getZoom() ?? DEFAULT_ZOOM;
        const toZoom = latitudeDeltaToZoom(region.latitudeDelta);
        const span = Math.max(
          Math.abs(region.latitude - fromCenter.lat()),
          Math.abs(region.longitude - fromCenter.lng()),
        );

        const mapInstance = map;
        // ~0.3° ≈ city-size area — smooth fly-to within this range
        if (span < 0.3) {
          if (Math.abs(fromZoom - toZoom) > 0.5) {
            mapInstance.setZoom(toZoom);
          }
          animateCameraCenter(mapInstance, region, 0, 0, duration);
        } else {
          cancelCameraAnimation();
          // Far away: snap to position zoomed out slightly, then ease in
          // Floor to integer so we land on a clean tile level (avoids mid-transition tile color flicker)
          const landingZoom = Math.floor(toZoom);
          mapInstance.moveCamera({
            center: { lat: region.latitude, lng: region.longitude },
            zoom: landingZoom,
          });
          // Ease zoom in over 400ms
          const start = performance.now();
          function step(now: number) {
            const t = Math.min((now - start) / 400, 1);
            const ease = 1 - (1 - t) * (1 - t); // ease-out quad
            mapInstance.moveCamera({
              center: { lat: region.latitude, lng: region.longitude },
              zoom: landingZoom + (toZoom - landingZoom) * ease,
            });
            if (t < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        }
      },
      fitToCoordinates(
        coords: Array<{ latitude: number; longitude: number }>,
        options?: {
          edgePadding?: {
            top: number;
            right: number;
            bottom: number;
            left: number;
          };
        },
      ) {
        if (!map) return;
        const bounds = new google.maps.LatLngBounds();
        coords.forEach((c) =>
          bounds.extend({ lat: c.latitude, lng: c.longitude }),
        );
        const padding = options?.edgePadding ?? 50;
        map.fitBounds(bounds, padding);
      },
      focusWithOffset(
        target: { latitude: number; longitude: number },
        offsetX: number,
        zoom?: number,
      ) {
        const run = (m: google.maps.Map) => {
          if (zoom !== undefined) m.setZoom(zoom);
          // Park the pin LOW: its popover opens upward, so the flight buys
          // the room above the pin rather than centering it.
          const park =
            -(m.getDiv()?.clientHeight ?? 0) * SELECTION_PARK_FRACTION;
          animateCameraCenter(m, target, offsetX / 2, park, CAMERA_FOCUS_DURATION_MS);
        };
        if (!map) {
          pendingCallsRef.current.push(run);
          return;
        }
        run(map);
      },
      zoomIn() {
        if (!map) return;
        const current = map.getZoom() ?? DEFAULT_ZOOM;
        map.setZoom(Math.min(Math.round(current) + 1, MAX_ZOOM));
      },
      zoomOut() {
        if (!map) return;
        const current = map.getZoom() ?? DEFAULT_ZOOM;
        map.setZoom(Math.max(Math.round(current) - 1, MIN_ZOOM));
      },
    }),
    [animateCameraCenter, cancelCameraAnimation, map],
  );

  // Notify parent when user starts interacting (drag or zoom) so it can
  // freeze displayed markers until the map settles.
  useEffect(() => {
    if (!map) return;
    const handleStart = () => {
      cancelCameraAnimation();
      onInteractionStartRef.current?.();
    };
    const dragListener = map.addListener("dragstart", handleStart);
    const zoomListener = map.addListener("zoom_changed", handleStart);
    return () => {
      dragListener.remove();
      zoomListener.remove();
    };
  }, [cancelCameraAnimation, map]);

  // Sync region state when map becomes idle (after drag/zoom)
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", () => {
      const center = map.getCenter();
      const mapBounds = map.getBounds();
      if (!center || !mapBounds) return;
      const ne = mapBounds.getNorthEast();
      const sw = mapBounds.getSouthWest();
      onRegionChangeRef.current({
        latitude: center.lat(),
        longitude: center.lng(),
        latitudeDelta: ne.lat() - sw.lat(),
        longitudeDelta: ne.lng() - sw.lng(),
        zoom: map.getZoom(),
      });
    });
    return () => listener.remove();
  }, [map]);

  // Forward map clicks on empty space to parent. Clicks on markers (pins,
  // clusters, the infobox) never reach this listener — they are clickable
  // markers, so Google's hit-testing consumes them.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      const latLng = e.latLng;
      if (!latLng) return;
      cancelCameraAnimation();
      onMapClickRef.current?.({
        latitude: latLng.lat(),
        longitude: latLng.lng(),
      });
    });
    return () => listener.remove();
  }, [cancelCameraAnimation, map]);

  return (
    <>
      {clusterPresence.map(({ key, item: cluster, exiting }) => (
        <ClusterMarker
          key={key}
          cluster={cluster}
          outlineColor={mapColors.outlineColor}
          clusterFill={mapColors.clusterFill}
          clusterText={mapColors.clusterText}
          onClick={onClusterPress}
          exiting={exiting}
        />
      ))}
      {pinPresence.map(({ key, item: pin, exiting }) => (
        <PinMarker
          key={key}
          pin={pin}
          outlineColor={mapColors.outlineColor}
          pinOverlay={mapColors.pinOverlay}
          pinOverlayText={mapColors.pinOverlayText}
          onClick={onPinPress}
          onHover={onPinHover}
          exiting={exiting}
          selected={pin.id === selectedPin?.id}
        />
      ))}
      {userLocation && (
        <UserLocationMarker
          coordinate={{
            latitude: userLocation.latitude,
            longitude: userLocation.longitude,
          }}
          heading={userLocation.heading}
        />
      )}

      {selectedPin ? (
        <SelectionEntranceGate
          key={`${selectedPin.id}:${selectedPin.latitude}:${selectedPin.longitude}`}
          map={map}
          pin={selectedPin}
        >
          {/* Local pins use a dedicated plus-marker path. The keyed gate holds
              both marker and card until the camera brings the anchor safely
              inside the viewport, so neither entrance finishes while clipped. */}
          {selectedPin.isLocal && selectedPin.issueIds.length === 0 ? (
            <NewLocationPinMarker
              latitude={selectedPin.latitude}
              longitude={selectedPin.longitude}
              outlineColor={mapColors.outlineColor}
              pinOverlay={mapColors.pinOverlay}
              pinOverlayText={mapColors.pinOverlayText}
            >
              {showInfobox ? (
                <Box
                  bottom={59}
                  left={centeredOverlayLeft(INFOBOX_WIDTH, MAP_PIN_WIDTH)}
                  position="absolute"
                >
                  <MapInfoboxTransition staggerWithMarker>
                    <Box
                      className={isDark ? undefined : "drop-shadow-md"}
                      id={MAP_INFOBOX_ID}
                      ref={containMapEvents}
                    >
                      <MapInfoboxContent
                        pin={selectedPin}
                        showPropertyFacts={mapSettings.propertyFacts}
                        onClose={onInfoboxClose}
                        onAddProject={
                          onAddProject ? handleAddProject : undefined
                        }
                      />
                      <MapInfoboxCaret />
                    </Box>
                  </MapInfoboxTransition>
                </Box>
              ) : null}
            </NewLocationPinMarker>
          ) : null}
          {selectedPin.isLocal && selectedPin.issueIds.length > 0 ? (
            <PinMarker
              pin={selectedPin}
              outlineColor={mapColors.outlineColor}
              pinOverlay={mapColors.pinOverlay}
              pinOverlayText={mapColors.pinOverlayText}
              selected
            />
          ) : null}

          {/* Existing project pins stay mounted. Only their desktop infobox
              waits for the selected anchor to enter the safe viewport. */}
          {showInfobox &&
          (!selectedPin.isLocal || selectedPin.issueIds.length > 0) ? (
            <AdvancedMarker
              position={{
                lat: selectedPin.latitude,
                lng: selectedPin.longitude,
              }}
              zIndex={1000}
              onClick={consumeMarkerClick}
            >
              <Box
                bottom={59}
                left={centeredOverlayLeft(INFOBOX_WIDTH, 0)}
                position="absolute"
              >
                {/* id: stable hook for the e2e suite's infobox assertions — the
                    overlay lives inside the Google Maps DOM, where structural
                    selectors are not stable. */}
                <MapInfoboxTransition>
                  <Box
                    className={isDark ? undefined : "drop-shadow-md"}
                    id={MAP_INFOBOX_ID}
                    ref={containMapEvents}
                  >
                    <MapInfoboxContent
                      pin={selectedPin}
                      showPropertyFacts={mapSettings.propertyFacts}
                      onClose={onInfoboxClose}
                      onAddProject={
                        onAddProject ? handleAddProject : undefined
                      }
                    />
                    <MapInfoboxCaret />
                  </Box>
                </MapInfoboxTransition>
              </Box>
            </AdvancedMarker>
          ) : null}
        </SelectionEntranceGate>
      ) : null}
    </>
  );
}

export function NativeMapView({
  ref,
  ...props
}: MapSurfaceProps & Omittable<{ ref?: React.Ref<MapViewRef> }>) {
  const { mapSettings, initialRegion, onRegionChangeComplete } = props;
  const tileTheme = useMapTileTheme(mapSettings.mapShade);
  const isTouch = useIsTouchDevice();
  // Build a key that forces a full Map remount when settings or the tile
  // theme change. The theme cannot restyle in place (see the styler effect),
  // so its identity — every color the styler carries — rides the key.
  const themeKey = tileTheme.style
    .map((element) => element.stylers[0]?.color ?? "")
    .join("");
  const mapKey = `${themeKey}-${mapSettings.mapTypeId}-${mapSettings.roadNames}-${mapSettings.pointsOfInterest}-${mapSettings.transitLines}`;

  // Read at each Map mount — the Map only reads `defaultCenter`/`defaultZoom`
  // on its first render, so between remounts the ref's updates are inert.
  const initialMapStateRef = useRef({
    center: initialRegion
      ? { lat: initialRegion.latitude, lng: initialRegion.longitude }
      : { lat: DEFAULT_CENTER.latitude, lng: DEFAULT_CENTER.longitude },
    zoom: initialRegion
      ? (initialRegion.zoom ?? Math.round(latitudeDeltaToZoom(initialRegion.latitudeDelta)))
      : DEFAULT_ZOOM,
  });
  // Track the live camera into the mount seed, so a settings- or theme-keyed
  // remount reopens exactly where the user was rather than at the original
  // initial region.
  const handleRegionChangeComplete = useCallback(
    (region: Region) => {
      initialMapStateRef.current = {
        center: { lat: region.latitude, lng: region.longitude },
        zoom:
          region.zoom ?? Math.round(latitudeDeltaToZoom(region.latitudeDelta)),
      };
      onRegionChangeComplete(region);
    },
    [onRegionChangeComplete],
  );

  return (
    <Box background="background" fill="flex" flex={1}>
      <APIProvider apiKey={API_KEY} libraries={["places"]}>
        <Map
          key={mapKey}
          mapId={MAP_ID}
          defaultCenter={initialMapStateRef.current.center}
          defaultZoom={initialMapStateRef.current.zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI
          // A pointer zooms by wheel and by the overlay's buttons; a double
          // click is more often a mis-fired pair of pin clicks than a zoom.
          // Touch has no buttons (the overlay hides them), so double-tap
          // stays on: it is the one-handed zoom every mapping app ships.
          disableDoubleClickZoom={!isTouch}
          clickableIcons={false}
          backgroundColor={tileTheme.groundHex}
        >
          <MapInternals
            ref={ref}
            {...props}
            onRegionChangeComplete={handleRegionChangeComplete}
            tileTheme={tileTheme}
          />
        </Map>
      </APIProvider>
    </Box>
  );
}
